import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { CityStats, Milestone, Scenario, ScenarioObjective } from '../../types';
import { playUiSfx } from '../../utils/uiAudio';
import { ModalShell, PanelHeader, buttons, surfaces, type as typeTokens, cn } from './kit';
import {
  formatRunSummaryText,
  recordLeaderboardEntry,
  type LeaderboardEntry,
  type LeaderboardOutcome,
} from '../../utils/localLeaderboard';
import {
  buildCompletionReview,
  formatCareerExportLine,
  getCareerSummary,
  recordRunCompletion,
  updateLatestRunQuiz,
  type CompletionReview,
  type RunCompletionRecord,
} from '../../utils/playerAnalytics';
import { TUTORIAL_CAMPAIGN } from '../../constants';

interface RunSummaryModalProps {
  gameStatus: LeaderboardOutcome;
  stats: CityStats;
  tickNumber: number;
  activeScenario?: Scenario | null;
  milestones: Milestone[];
  scenarioObjectives: ScenarioObjective[];
  onRestart: () => void;
  onExtend?: () => void;
  onRetry?: () => void;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value);
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

const RunSummaryModal: React.FC<RunSummaryModalProps> = ({
  gameStatus,
  stats,
  tickNumber,
  activeScenario,
  milestones,
  scenarioObjectives,
  onRestart,
  onExtend,
  onRetry,
}) => {
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [rank, setRank] = useState<number | undefined>();
  const [score, setScore] = useState<number | undefined>();
  const [copied, setCopied] = useState(false);
  const [quizAnswer, setQuizAnswer] = useState<string | null>(null);
  const [quizHint, setQuizHint] = useState<string | null>(null);
  const [completionReview, setCompletionReview] = useState<CompletionReview | null>(null);
  const recordedRef = useRef(false);
  const runRecordRef = useRef<RunCompletionRecord | null>(null);

  const assessmentQuestion = activeScenario?.questions?.[0];
  const quizLetter = (option: string) => option.trim().charAt(0).toUpperCase();

  const isLoss = gameStatus === 'lost';
  const isScenarioRun = Boolean(activeScenario);
  const historicalDebrief = activeScenario
    ? (isLoss
        ? (activeScenario.debriefLoss ?? activeScenario.historicalOutcome)
        : (activeScenario.debriefWin ?? activeScenario.historicalOutcome))
    : undefined;
  const completedMilestones = milestones.filter((m) => m.completed);
  const completedObjectives = scenarioObjectives.filter((o) => o.completed);
  const missedObjectives = scenarioObjectives.filter((o) => !o.completed);

  useEffect(() => {
    if (recordedRef.current) return;
    recordedRef.current = true;

    const result = recordLeaderboardEntry(activeScenario?.id, {
      compliance: stats.compliance,
      budget: stats.budget,
      threat: stats.threat,
      day: stats.day,
      outcome: gameStatus,
    });

    const analyticsRecord = recordRunCompletion({
      scenarioId: activeScenario?.id,
      scenarioTitle: activeScenario?.title ?? 'Sandbox Mode',
      outcome: gameStatus,
      compliance: stats.compliance,
      budget: stats.budget,
      threat: stats.threat,
      day: stats.day,
      tickNumber,
      milestonesCompleted: milestones.filter((m) => m.completed).length,
      milestonesTotal: milestones.length,
      objectivesCompleted: scenarioObjectives.filter((o) => o.completed).length,
      objectivesTotal: scenarioObjectives.length,
      rank: result.rank,
    });
    runRecordRef.current = analyticsRecord;
    setCompletionReview(buildCompletionReview(analyticsRecord));

    setLeaderboard(result.leaderboard);
    setRank(result.rank);
    setScore(result.entry.score);
  }, [
    activeScenario?.id,
    activeScenario?.title,
    gameStatus,
    milestones,
    scenarioObjectives,
    stats.budget,
    stats.compliance,
    stats.day,
    stats.threat,
    tickNumber,
  ]);

  const summaryText = useMemo(
    () =>
      formatRunSummaryText({
        gameStatus,
        compliance: stats.compliance,
        budget: stats.budget,
        threat: stats.threat,
        day: stats.day,
        tickNumber,
        activeScenario,
        milestones,
        scenarioObjectives,
        score,
        rank,
        completionGrade: completionReview?.grade,
        completionHeadline: completionReview?.headline,
      }),
    [
      gameStatus,
      stats,
      tickNumber,
      activeScenario,
      milestones,
      scenarioObjectives,
      score,
      rank,
      completionReview,
    ],
  );

  const careerLine = useMemo(
    () => formatCareerExportLine(getCareerSummary(TUTORIAL_CAMPAIGN.length)),
    [completionReview],
  );

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(summaryText);
      setCopied(true);
      playUiSfx('success');
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      playUiSfx('click');
    }
  };

  const handleShare = async () => {
    const title = 'Compliance Command — Run Summary';
    try {
      if (typeof navigator.share === 'function') {
        await navigator.share({ title, text: summaryText });
        playUiSfx('success');
        return;
      }
      await navigator.clipboard.writeText(summaryText);
      setCopied(true);
      playUiSfx('success');
      window.setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      playUiSfx('click');
    }
  };

  const headline = isLoss ? 'SYSTEM FAILURE' : 'MISSION ACCOMPLISHED';
  const subline = isLoss
    ? 'Federal regulators have seized all assets due to compliance negligence.'
    : gameStatus === 'scenario_won'
      ? 'Objectives complete. System stable and secure.'
      : 'IPO successful. You built a financial empire.';

  const topThree = leaderboard.slice(0, 3);

  return (
    <ModalShell
      variant="center"
      zLevel="menu"
      overlayClassName="bg-black/90 overflow-y-auto overscroll-contain"
      className="max-w-2xl"
      testId="run-summary-modal"
    >
      <div
        className={cn(
          surfaces.panel,
          'p-0 shadow-2xl relative overflow-hidden my-4 border-2',
          isScenarioRun
            ? isLoss
              ? 'border-red-500/60'
              : 'border-emerald-500/60'
            : 'border-slate-600',
        )}
      >
        <div
          className={cn(
            'absolute top-0 left-0 w-full h-1',
            isLoss ? 'bg-gradient-to-r from-red-600 to-red-400' : 'bg-gradient-to-r from-emerald-500 to-cyan-500',
          )}
        />

        <PanelHeader
          title={headline}
          subtitle={activeScenario?.title ?? 'Sandbox Mode'}
          eyebrow="Run Summary"
          icon={isLoss ? '⚠️' : '🏆'}
          className="px-6 md:px-8"
        />

        <div className="px-6 md:px-8 pb-8 space-y-6">
          <p className={cn(typeTokens.body, 'text-center')}>{subline}</p>

          {completionReview && (
            <div
              className="p-4 bg-slate-900/70 border border-slate-700 rounded-lg"
              data-testid="run-summary-completion-review"
            >
              <div className="flex items-center justify-between gap-3 mb-2">
                <p className={typeTokens.eyebrowAccent}>Completion Review</p>
                <span
                  className={cn(
                    'text-lg font-black font-mono px-3 py-1 rounded border',
                    completionReview.grade === 'A' || completionReview.grade === 'B'
                      ? 'text-emerald-300 border-emerald-600/50 bg-emerald-950/40'
                      : completionReview.grade === 'C'
                        ? 'text-amber-300 border-amber-600/50 bg-amber-950/30'
                        : 'text-red-300 border-red-600/50 bg-red-950/30',
                  )}
                  data-testid="run-summary-grade"
                >
                  {completionReview.grade}
                </span>
              </div>
              <p className="text-sm text-slate-300">{completionReview.headline}</p>
              {completionReview.highlights.length > 0 && (
                <ul className="mt-3 space-y-1 text-xs text-emerald-300/90">
                  {completionReview.highlights.map((line) => (
                    <li key={line}>✓ {line}</li>
                  ))}
                </ul>
              )}
              {completionReview.improvements.length > 0 && (
                <ul className="mt-2 space-y-1 text-xs text-amber-300/90">
                  {completionReview.improvements.map((line) => (
                    <li key={line}>→ {line}</li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {isScenarioRun && historicalDebrief && (
            <div className="p-4 bg-indigo-950/30 border border-indigo-500/30 rounded-lg text-center">
              <p className={typeTokens.eyebrow}>
                {isLoss ? 'Lessons Learned' : 'Historical Audit'}
              </p>
              <p className="text-sm italic text-indigo-200 mt-2 leading-relaxed">
                &ldquo;{historicalDebrief}&rdquo;
              </p>
            </div>
          )}

          {isScenarioRun && assessmentQuestion && (
            <div
              className="p-4 bg-slate-900/60 border border-slate-700 rounded-lg"
              data-testid="run-summary-quiz"
            >
              <p className={typeTokens.eyebrowAccent}>Knowledge Check</p>
              <p className="text-sm text-white mt-2 leading-relaxed">{assessmentQuestion.q}</p>
              <div className="mt-3 flex flex-col gap-2">
                {assessmentQuestion.options.map((option) => {
                  const letter = quizLetter(option);
                  const isSelected = quizAnswer === letter;
                  const isCorrect = assessmentQuestion.correct === letter;
                  return (
                    <button
                      key={option}
                      type="button"
                      data-testid={`run-summary-quiz-option-${letter}`}
                      onClick={() => {
                        setQuizAnswer(letter);
                        setQuizHint(isCorrect ? null : assessmentQuestion.hint);
                        updateLatestRunQuiz(isCorrect);
                        if (runRecordRef.current) {
                          const patched = {
                            ...runRecordRef.current,
                            quizAttempted: true,
                            quizPassed: isCorrect,
                          };
                          runRecordRef.current = patched;
                          setCompletionReview(buildCompletionReview(patched));
                        }
                        playUiSfx(isCorrect ? 'success' : 'click');
                      }}
                      className={cn(
                        'text-left text-sm px-3 py-2 rounded-md border transition-colors',
                        isSelected
                          ? isCorrect
                            ? 'border-emerald-500/60 bg-emerald-950/40 text-emerald-200'
                            : 'border-red-500/50 bg-red-950/30 text-red-200'
                          : 'border-slate-700 bg-slate-950/50 text-slate-300 hover:border-slate-500',
                      )}
                    >
                      {option}
                    </button>
                  );
                })}
              </div>
              {quizAnswer && assessmentQuestion.correct === quizAnswer && (
                <p className="text-xs text-emerald-400 mt-3 font-mono" data-testid="run-summary-quiz-pass">
                  Correct — controls validated.
                </p>
              )}
              {quizHint && (
                <p className="text-xs text-amber-300/90 mt-3 font-mono" data-testid="run-summary-quiz-hint">
                  Hint: {quizHint}
                </p>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: 'Compliance', value: `${Math.round(stats.compliance)}%`, accent: 'text-emerald-400' },
              { label: 'Budget', value: formatCurrency(stats.budget), accent: 'text-cyan-400' },
              { label: 'Threat', value: `${Math.round(stats.threat)}`, accent: 'text-red-400' },
              { label: 'Day', value: `${stats.day}`, accent: 'text-amber-400' },
            ].map((stat) => (
              <div key={stat.label} className="bg-slate-900/60 border border-slate-800 rounded-lg p-3 text-center">
                <p className={typeTokens.eyebrow}>{stat.label}</p>
                <p className={cn('font-mono font-bold text-lg mt-1', stat.accent)}>{stat.value}</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-slate-900/40 border border-slate-800 rounded-lg p-4">
              <p className={typeTokens.eyebrowAccent}>Milestones</p>
              <p className="text-white font-bold mt-1">
                {completedMilestones.length}/{milestones.length} complete
              </p>
              <ul className="mt-3 space-y-1 max-h-28 overflow-y-auto">
                {milestones.map((m) => (
                  <li
                    key={m.id}
                    className={cn('text-xs', m.completed ? 'text-emerald-300' : 'text-slate-500')}
                  >
                    {m.completed ? '✓' : '○'} {m.name}
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-slate-900/40 border border-slate-800 rounded-lg p-4">
              <p className={typeTokens.eyebrowAccent}>
                {scenarioObjectives.length > 0 ? 'Scenario Objectives' : 'Session'}
              </p>
              {scenarioObjectives.length > 0 ? (
                isScenarioRun ? (
                  <>
                    <div className="flex justify-between items-center mt-2 mb-3">
                      <p className="text-white font-bold">Objective Status</p>
                      <span
                        className={cn(
                          'text-xs font-mono font-bold px-2 py-0.5 rounded',
                          isLoss ? 'text-amber-400 bg-amber-950/50' : 'text-emerald-400 bg-emerald-950/50',
                        )}
                      >
                        {completedObjectives.length} / {scenarioObjectives.length} MET
                      </span>
                    </div>
                    <ul className="space-y-2 max-h-40 overflow-y-auto">
                      {scenarioObjectives.map((obj, i) => (
                        <li
                          key={`${obj.description}-${i}`}
                          className={cn(
                            'flex items-start gap-3 text-sm p-3 rounded-lg border',
                            obj.completed
                              ? 'text-emerald-300 bg-emerald-950/30 border-emerald-500/30'
                              : 'text-slate-300 bg-red-950/20 border-red-500/20',
                          )}
                        >
                          <span className="mt-0.5 text-base shrink-0">{obj.completed ? '✓' : '✗'}</span>
                          <span className={obj.completed ? 'line-through opacity-80' : ''}>
                            {obj.description}
                          </span>
                        </li>
                      ))}
                    </ul>
                    {isLoss && missedObjectives.length > 0 && (
                      <p className="mt-3 text-xs text-red-300/90 font-mono">
                        Incomplete: {missedObjectives.map((o) => o.description).join('; ')}
                      </p>
                    )}
                  </>
                ) : (
                  <>
                    <p className="text-white font-bold mt-1">
                      {completedObjectives.length}/{scenarioObjectives.length} complete
                    </p>
                    <ul className="mt-3 space-y-1 max-h-28 overflow-y-auto">
                      {scenarioObjectives.map((obj, i) => (
                        <li
                          key={`${obj.description}-${i}`}
                          className={cn('text-xs', obj.completed ? 'text-emerald-300' : 'text-slate-500')}
                        >
                          {obj.completed ? '✓' : '○'} {obj.description}
                        </li>
                      ))}
                    </ul>
                  </>
                )
              ) : (
                <p className="text-sm text-slate-400 mt-2">
                  Tick {tickNumber} · Score {score?.toFixed(2) ?? '—'}
                </p>
              )}
            </div>
          </div>

          <div className="bg-slate-900/40 border border-slate-800 rounded-lg p-4">
            <div className="flex items-center justify-between gap-2 mb-3">
              <p className={typeTokens.eyebrowAccent}>Local Leaderboard</p>
              <span className={typeTokens.caption}>On this device · top 3</span>
            </div>
            {topThree.length === 0 ? (
              <p className="text-sm text-slate-500">No entries yet.</p>
            ) : (
              <ol className="space-y-2">
                {topThree.map((entry, index) => (
                  <li
                    key={entry.date}
                    className={cn(
                      'flex items-center justify-between text-sm rounded-md px-3 py-2',
                      index === 0 && rank === 1 ? 'bg-cyan-900/30 border border-cyan-700/40' : 'bg-slate-950/50',
                    )}
                  >
                    <span className="text-slate-300">
                      <span className="font-mono text-cyan-400 mr-2">#{index + 1}</span>
                      {entry.playerLabel}
                      <span className="text-slate-500 ml-2 text-xs">{formatDate(entry.date)}</span>
                    </span>
                    <span className="font-mono font-bold text-white">{entry.score.toFixed(2)}</span>
                  </li>
                ))}
              </ol>
            )}
            {rank !== undefined && (
              <p className="text-xs text-slate-500 mt-3 font-mono">
                Your rank: #{rank} · Score {score?.toFixed(2)}
              </p>
            )}
            {careerLine && (
              <p className="text-xs text-indigo-300/80 mt-2 font-mono" data-testid="run-summary-career-line">
                {careerLine}
              </p>
            )}
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              data-testid="run-summary-copy"
              onClick={() => { void handleCopy(); }}
              className={cn(buttons.ghost, 'flex-1 py-3')}
            >
              {copied ? 'Copied!' : 'Copy Summary'}
            </button>
            <button
              type="button"
              data-testid="run-summary-share"
              onClick={() => { void handleShare(); }}
              className={cn(buttons.primary, 'flex-1 py-3')}
            >
              Share
            </button>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 pt-1">
            <button
              type="button"
              data-testid="run-summary-reboot"
              onClick={() => { playUiSfx('click'); onRestart(); }}
              className={cn(activeScenario ? buttons.ghost : buttons.resume, 'flex-1 py-4')}
            >
              {activeScenario ? 'Return to Menu' : 'Reboot System'}
            </button>
            {onRetry && (
              <button
                type="button"
                data-testid="run-summary-retry"
                onClick={() => { playUiSfx('success'); onRetry(); }}
                className={cn(buttons.primary, 'flex-[2] py-4')}
              >
                {gameStatus === 'scenario_won' ? 'Replay Mission' : 'Retry Mission'}
              </button>
            )}
            {isLoss && !activeScenario && onExtend && (
              <button
                type="button"
                data-testid="run-summary-bailout"
                onClick={() => { playUiSfx('click'); onExtend(); }}
                className={cn(buttons.ghost, 'flex-1 py-4 border-white/20 text-white hover:bg-white/10')}
              >
                Emergency Bailout
              </button>
            )}
          </div>
        </div>
      </div>
    </ModalShell>
  );
};

export default RunSummaryModal;